import {
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  Drawer,
  DrawerOverlay,
  Flex,
  Heading,
  Divider,
  Button,
  DrawerFooter,
  useToast,
  Alert,
  AlertIcon,
  Skeleton,
  VStack,
  HStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { getInvoiceComputedStatus } from '../utils';
import { InvoiceDetailActionsMenu } from './InvoiceDetailActionsMenu';
import { Icon } from 'packages/common-frontend/components/icons';
import { successToastConfig } from 'packages/common-frontend/constants';
import { truncateUuid } from 'packages/common-frontend/utils';
import {
  GetInvoiceByIdDocumentGQLType,
  GetInvoicesByLoanIdDocumentGQLType,
  GetInvoicesByOrganizationIdDocumentGQLType,
  GetPaymentInKindServicingFeeDetailsForCreditFacilitiesDocumentGQLType,
  GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
  useGetInvoiceByIdQuery,
  useReverseInvoicePaymentMutation,
} from 'packages/invoices/requests/invoices.gql.generated';
import { InvoiceDetailInformation } from 'packages/invoices/ui/InvoiceDetailInformation';
import { InvoiceStatusBadge } from 'packages/invoices/ui/InvoiceStatusBadge';
import { GetResolvedLoanByIdDocumentGQLType } from 'packages/loans/requests/loans.gql.generated';
import { TaskDetails } from 'packages/tasks/ui/TaskEntityDetails';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
  GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
} from 'packages/transactions/requests/transactions.gql.generated';

type InvoiceDetailSideDrawerHeaderProps = {
  invoiceId: string;
  onCloseDrawer: () => void;
};
const InvoiceDetailSideDrawerHeader = ({
  invoiceId,
  onCloseDrawer,
}: InvoiceDetailSideDrawerHeaderProps) => {
  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        m="24px 24px 0 0"
      >
        <Heading
          as="h3"
          fontSize="20px"
          lineHeight="28px"
          fontWeight={600}
          m="24px 24px 24px 40px"
        >
          {`Invoice ${truncateUuid(invoiceId)}`}
        </Heading>
        <Button variant="v2-ghost" onClick={onCloseDrawer}>
          <Icon icon="x" />
        </Button>
      </Flex>
      <Divider />
    </>
  );
};

type InvoiceDetailSideDrawerProps = {
  invoiceId: string;
  isOpen: boolean;
  onClose: () => void;
  taskId?: string;
};
export const InvoiceDetailSideDrawer = ({
  invoiceId,
  isOpen,
  onClose,
  taskId,
}: InvoiceDetailSideDrawerProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [
    reverseInvoicePayment,
    { loading: isReverseInvoicePaymentLoading },
  ] = useReverseInvoicePaymentMutation({
    refetchQueries: [
      GetInvoiceByIdDocumentGQLType,
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetInvoicesByLoanIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
      GetPaymentInKindServicingFeeDetailsForCreditFacilitiesDocumentGQLType,
      GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
      // refetch loan transactions when reversing payment from side drawer
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
      GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
    ],
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully reversed invoice payment',
      });
    },
  });

  const {
    data: invoiceData,
    loading: isInvoiceLoading,
  } = useGetInvoiceByIdQuery({
    variables: { invoiceId },
  });

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader padding="0">
          <InvoiceDetailSideDrawerHeader
            invoiceId={invoiceId}
            onCloseDrawer={onClose}
          />
        </DrawerHeader>

        {isInvoiceLoading || !invoiceData ? (
          <Skeleton h="400px" />
        ) : (
          <DrawerBody p="40px">
            <VStack align="flex-start" gap="2rem">
              {isReverseInvoicePaymentLoading && (
                <Alert status="loading" mb="24px">
                  <AlertIcon />
                  Reversing invoice payment
                </Alert>
              )}
              <HStack justifyContent="space-between" w="100%">
                <InvoiceStatusBadge
                  computedStatus={getInvoiceComputedStatus({
                    status: invoiceData.getInvoiceById.status,
                    dueDate: invoiceData.getInvoiceById.dueDate,
                    targetInvoiceDate:
                      invoiceData.getInvoiceById.targetInvoiceDate ?? undefined,
                  })}
                  useBackgroundColors
                />
                <InvoiceDetailActionsMenu
                  invoice={invoiceData.getInvoiceById}
                  reverseInvoicePayment={() =>
                    reverseInvoicePayment({
                      variables: { invoiceId },
                    })
                  }
                  isReverseInvoicePaymentLoading={
                    isReverseInvoicePaymentLoading
                  }
                />
              </HStack>

              {taskId && (
                <>
                  <TaskDetails taskId={taskId} />
                  <Divider />
                </>
              )}

              <InvoiceDetailInformation
                invoice={invoiceData.getInvoiceById}
                mismatchAlertOnClick={() => {
                  navigate(`/invoices/${invoiceId}`);
                }}
                mismatchAlertOnClickLabel="View Invoice"
              />
            </VStack>
          </DrawerBody>
        )}
        <DrawerFooter>
          <Flex w="100%" justifyContent="flex-end">
            <Button
              as={RouterLink}
              to={`/invoices/${invoiceId}`}
              rightIcon={<Icon icon="chevron-right" />}
              variant="v2-ghost"
            >
              View Invoice
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
